import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";
import { ControlExecutionDataService } from "../services/control-execution-data.service";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { nameof } from "@methods/CommonMethods";
import { TaskSeriesOverviewModel } from "../control-execution-detail/models/task-series-overview-model";
import { ControlExecutionOverviewModel } from "../../models/control-execution/control-execution-overview-model";
import { RendererType } from "@enums/RendererType";
import { FormatType } from "@enums/FormatType";
import { isGuid } from "@methods/uniqueMethods";
import { lastValueFrom, firstValueFrom } from "rxjs";
import { ControlExecutionPermissionModel } from "../control-execution-detail/models/control-execution-permission-model";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";

@Component({
    selector: "control-execution-overview",
    templateUrl: "./control-execution-overview.component.html",
    standalone: false,
    providers: [provideTranslocoScope("controls")],
})
export class ControlExecutionOverviewComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _ds = inject(ControlExecutionDataService);
    private _appSettingDS = inject(SettingsDataService);
    private _transloco = inject(TranslocoService);

    protected id: string;
    protected tab: TabModel;

    protected recentUpcomingConfig: GenericListConfig;
    protected taskSeriesConfig: GenericListConfig;
    protected allControlExecutionsConfig: GenericListConfig;
    permissions: ControlExecutionPermissionModel;
    isLoaded: Boolean = false;

    public async ngOnInit(): Promise<void> {
        this.id = this.route.snapshot.params.id ? this.route.snapshot.params.id : this.tab.id;

        if (!isGuid(this.id)) {
            this.tab.close(false);
            return;
        }

        this.permissions = await lastValueFrom(this._ds.getPermissions());

        await firstValueFrom(this._transloco.load(`controls/${this._transloco.getActiveLang()}`));

        this.createRecentAndUpcomingConfig();
        this.createTaskSeriesConfig();
        this.createAllControlExecutionConfig();

        this.tab.showLoader = false;
        this.isLoaded = true;
    }

    private createRecentAndUpcomingConfig(): void {
        this.recentUpcomingConfig = <GenericListConfig>{
            name: this._transloco.translate("controls.executionOverview.recentUpcomingTitle"),
            emptyMessage: this._transloco.translate("controls.executionOverview.recentUpcomingEmptyMessage"),
            idProp: nameof<ControlExecutionOverviewModel>("guid"),
            uiid: "control-execution-recent-upcoming-overview",
            isSortable: false,

            allowAdd: false,
            allowEdit: true,
            allowDelete: false,

            fields: [
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("actionDate"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldActionDate"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("statusDescription"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldStatus"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("name"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldName"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("responsibleNames"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldResponsibles"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("reviewerNames"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldReviewers"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "scoreName",
                    prettyName: this._transloco.translate("controls.executionOverview.fieldScore"),
                    fieldType: GenericListFieldType.ColoredSelect,
                },
                {
                    fieldType: GenericListFieldType.Text,
                    fieldName: "scoreName_COLOR",
                    prettyName: this._transloco.translate("controls.executionOverview.fieldScoreColor"),
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("comments"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldComments"),
                    fieldType: GenericListFieldType.TextArea,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("completedAt"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldDateCompleted"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("completedBy"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldCompletedBy"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("documentCount"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldDocumentCount"),
                    fieldType: GenericListFieldType.Number,
                },
            ],

            rendererConfig: [
                {
                    textColumn: nameof<ControlExecutionOverviewModel>("actionDate"),
                    actionColumn: nameof<ControlExecutionOverviewModel>("actionDateExpired"),
                    actionValue: true,
                    type: RendererType.DueDate,
                    formatType: FormatType.DateFormat,
                },
                {
                    textColumn: "scoreName",
                    actionColumn: "scoreName_COLOR",
                    hideActionColumn: true,
                    type: RendererType.Score,
                },
            ],

            dataMethod: () => {
                return this._ds.getRecentAndUpcoming(this.id);
            },
            editOverride: (row) => {
                this._tabService.generateTab(
                    this._pages.TaskDetail, // ControlExecutionDetail,
                    row.guid,
                    this._transloco.translate("controls.executionOverview.executionTabTitle", { identifier: +row.identifier, name: row.name })
                );
            },
        };
    }

    private createTaskSeriesConfig() {
        if (this.taskSeriesConfig) {
            return;
        }

        this.taskSeriesConfig = <GenericListConfig>{
            name: this._transloco.translate("controls.executionOverview.taskSeriesTitle"),
            idProp: nameof<TaskSeriesOverviewModel>("guid"),
            uiid: "control-execution-task-series-overview",
            uiidAdd: "control-execution-task-series-overview-addbutton",
            isSortable: false,

            allowAdd: this.permissions.canCreateTaskseries,
            allowEdit: this.permissions.canEditTaskseries,
            allowDelete: false, // Only make task series deletable in the detail view

            hideDeleteButton: true,
            skipReloadConfirm: true,

            fields: [
                {
                    fieldName: nameof<TaskSeriesOverviewModel>("name"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldName"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<TaskSeriesOverviewModel>("startdate"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldStartDate"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<TaskSeriesOverviewModel>("enddate"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldEndDate"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<TaskSeriesOverviewModel>("responsibles"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldResponsibleS"),
                    fieldType: GenericListFieldType.Text,
                },
            ],

            dataMethod: () => {
                return this._ds.getTaskSeries(this.id);
            },
            addOverride: () => {
                this._tabService.generateTab(
                    this._pages.TaskSeriesDetail,
                    null,
                    this._transloco.translate("controls.executionOverview.newControlExecution"),
                    { controlGuid: this.id }
                );
            },
            editOverride: (row) => {
                this._tabService.generateTab(
                    this._pages.TaskSeriesDetail,
                    row.guid,
                    this._transloco.translate("controls.executionOverview.executionTabTitle", { identifier: +row.identifier, name: row.name })
                );
            },
        };
    }

    private createAllControlExecutionConfig(): void {
        if (this.allControlExecutionsConfig) {
            return;
        }

        this.allControlExecutionsConfig = <GenericListConfig>{
            name: this._transloco.translate("controls.executionOverview.allControlExecutionsTitle"),
            emptyMessage: this._transloco.translate("controls.executionOverview.allControlExecutionsEmptyMessage"),
            idProp: nameof<ControlExecutionOverviewModel>("guid"),
            uiid: "control-execution-overview",
            uiidAdd: "control-execution-overview-addbutton",
            isSortable: false,

            allowAdd: true,
            allowEdit: true,
            allowDelete: false,

            addButtonTooltip: this._transloco.translate("controls.executionOverview.addButtonTooltip"),

            fields: [
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("actionDate"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldActionDate"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("statusDescription"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldStatus"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("name"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldName"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("responsibleNames"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldResponsibles"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("reviewerNames"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldReviewers"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "scoreName",
                    prettyName: this._transloco.translate("controls.executionOverview.fieldScore"),
                    fieldType: GenericListFieldType.ColoredSelect,
                },
                {
                    fieldType: GenericListFieldType.Text,
                    fieldName: "scoreName_COLOR",
                    prettyName: this._transloco.translate("controls.executionOverview.fieldScoreColor"),
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("comments"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldComments"),
                    fieldType: GenericListFieldType.TextArea,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("completedAt"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldDateCompleted"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("completedBy"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldCompletedBy"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>("documentCount"),
                    prettyName: this._transloco.translate("controls.executionOverview.fieldDocumentCount"),
                    fieldType: GenericListFieldType.Number,
                },
            ],

            rendererConfig: [
                {
                    textColumn: nameof<ControlExecutionOverviewModel>("actionDate"),
                    actionColumn: nameof<ControlExecutionOverviewModel>("actionDateExpired"),
                    actionValue: true,
                    type: RendererType.DueDate,
                    formatType: FormatType.DateFormat,
                },
                {
                    textColumn: "scoreName",
                    actionColumn: "scoreName_COLOR",
                    hideActionColumn: true,
                    type: RendererType.Score,
                },
            ],

            dataMethod: () => {
                return this._ds.getControlExecutions(this.id);
            },
            addOverride: () => {
                this._tabService.generateTab(
                    this._pages.TaskDetail,
                    null,
                    this._transloco.translate("controls.executionOverview.newControlExecution"),
                    { controlGuid: this.id }
                );
            },
            editOverride: (row) => {
                this._tabService.generateTab(
                    this._pages.TaskDetail, // ControlExecutionDetail,
                    row.guid,
                    this._transloco.translate("controls.executionOverview.executionTabTitle", { identifier: +row.identifier, name: row.name })
                );
            },
        };
    }
}
